/**
 * BeHeard Shell Adapter
 *
 * Module-level constant that wires BeHeard's Redux store, hooks, and
 * menu configuration to the shared SuiteShell.
 *
 * This file is the ONLY bridge between the shared shell and BeHeard's
 * app-specific state. All other shell rendering is owned by @levelup/ui-shell.
 */

import { createSelector } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import type {
  SuiteShellAdapter,
  SuiteAppExtended,
  SwitchContextWorkspace,
  TestingModeState,
} from '@levelup/ui-shell';
import type { SwitchContextUser } from '@levelup/central-auth';
import type { RootState } from '../store';

import {
  selectSuiteAppsRaw,
  selectSwitchContextMeta,
  selectOtherWorkspaces,
  selectTokenWorkspaceId,
} from '../store/slices/centralAuthSlice';
import {
  selectBootCurrentWorkspace,
  selectBootUser,
  selectBootMeta,
  selectCreateWorkspaceUrl,
} from '../store/slices/bootContextSlice';
import { APP_ID } from '../config/centralAuth';
import { useShellAuth } from './hooks/useShellAuth';
import { useStaticMenuQuery } from './hooks/useStaticMenuQuery';
import { useUnreadCount } from '../hooks/useUnreadCount';
import { NotificationPanel } from '../components/notifications/NotificationPanel';
import { TestingModePanel } from '../components/TestingModePanel';
import { MENU_ROUTES, getMenuIcon } from './menuConfig';

// ============================================================
// Adapter (MODULE-LEVEL CONSTANT — required by SuiteShellProvider)
//
// All selectors that return new objects/arrays MUST be memoized so
// that `useSelector` does not see a fresh reference on every render.
// We use createSelector (from reselect, re-exported by RTK).
// ============================================================

// selectSuiteAppsRaw is already a stable Redux selector returning the
// in-state array reference; pass through directly.
const selectApps = (state: RootState): SuiteAppExtended[] =>
  selectSuiteAppsRaw(state);

const selectCurrentWorkspace = createSelector(
  [selectBootCurrentWorkspace],
  (ws): SwitchContextWorkspace | null => {
    if (!ws) return null;
    return { ...ws, type: 'business' } as SwitchContextWorkspace;
  },
);

const selectOtherWorkspacesTyped = createSelector(
  [selectOtherWorkspaces],
  (workspaces): SwitchContextWorkspace[] =>
    workspaces.map((ws) => ({ ...ws, type: 'business' as const })),
);

// SuiteShell expects strict SwitchContextUser shape:
//   { id, email, fullName, avatarUrl, platformInternal?, platformSuperAdmin? }
// BeHeard's Central token-exchange may store the user with `name` instead
// of `fullName`. Without this transform the shell footer falls back to
// rendering the email + a "?" placeholder for initials.
//
// Memoized so identical input → identical reference (no rerender storms).
const selectShellUser = createSelector(
  [selectBootUser],
  (raw): SwitchContextUser | null => {
    if (!raw) return null;
    const u = raw as Record<string, unknown>;
    return {
      id: String(u.id ?? ''),
      email: String(u.email ?? ''),
      fullName:
        (typeof u.fullName === 'string' && u.fullName) ||
        (typeof u.name === 'string' && u.name) ||
        null,
      avatarUrl:
        (typeof u.avatarUrl === 'string' && u.avatarUrl) || null,
      platformInternal: u.platformInternal === true ? true : undefined,
      platformSuperAdmin: u.platformSuperAdmin === true ? true : undefined,
    };
  },
);

// ============================================================
// Testing-mode hook — returns null to hide the flask icon.
// Visibility is gated on user.platformInternal && meta.features.testingMode.
// (Matches BrandTalk's gating exactly.)
// ============================================================

function useTestingModeState(): TestingModeState | null {
  const user = useSelector(selectBootUser);
  const meta = useSelector(selectBootMeta);

  // Visibility gate (matches BrandTalk):
  //   1. user must be platform-internal
  //   2. Central must have surfaced testing-mode for this session
  //      (meta.testingMode object present, OR meta.features.testingMode flag set)
  //
  // Cast to a permissive shape because BeHeard's local SwitchContextMeta
  // type may not have caught up with Central's testing-mode fields yet.
  const isPlatformInternal =
    (user as { platformInternal?: boolean } | null)?.platformInternal === true;

  const metaAny = meta as
    | {
        features?: Record<string, unknown>;
        testingMode?: {
          active?: boolean;
          override?: { workspaceId: string; appId: string; role: string };
        };
      }
    | null;

  const featureFlagOn = metaAny?.features?.testingMode === true;
  const tmFromMeta = metaAny?.testingMode;
  const testingModeAvailable = featureFlagOn || Boolean(tmFromMeta);

  if (!isPlatformInternal || !testingModeAvailable) return null;

  return {
    isActive: tmFromMeta?.active === true,
    override: tmFromMeta?.override as TestingModeState['override'],
  };
}

// ============================================================
// The adapter
// ============================================================

export const beheardShellAdapter: SuiteShellAdapter<RootState> = {
  selectors: {
    apps: selectApps,
    meta: selectSwitchContextMeta,
    currentWorkspace: selectCurrentWorkspace,
    user: selectShellUser,
    otherWorkspaces: selectOtherWorkspacesTyped,
    tokenWorkspaceId: selectTokenWorkspaceId,
    createWorkspaceUrl: selectCreateWorkspaceUrl,
  },

  hooks: {
    useAuth: useShellAuth,
    useGetMenuQuery: useStaticMenuQuery,
    useUnreadCount,
    useTestingModeState,
  },

  config: {
    appId: APP_ID,
    settingsRoute: '/settings',
    offloadRoute: '/offload-responsibilities',
  },

  adapters: {
    menuRoutes: MENU_ROUTES,
    getMenuIcon,
    NotificationPanel,
    TestingModePanel,
  },
};
